import { useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Doc } from '@cvx/_generated/dataModel'
import { Input } from '@rekrytering/ui/components/input'
import { Textarea } from '@rekrytering/ui/components/textarea'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TranslateButton } from '@/features/jobs/components/translate-button'

interface JobTranslationsEditorProps {
  readonly job: Doc<'jobs'>
}

export function JobTranslationsEditor({ job }: JobTranslationsEditorProps) {
  const { t } = useTranslation()

  const [titleDraft, setTitleDraft] = useState<string | null>(null)
  const [descriptionDraft, setDescriptionDraft] = useState<string | null>(null)

  const { mutate: saveTranslation } = useMutation({
    mutationFn: useConvexMutation(
      api.features.jobs.jobs.saveMyJobFieldTranslation,
    ),
  })

  const titleTranslation = titleDraft ?? job.titleTranslations?.['en'] ?? ''
  const descriptionTranslation =
    descriptionDraft ?? job.descriptionTranslations?.['en'] ?? ''

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <span className="text-xs font-medium text-primary/60">
            {t('jobs.translations.swedish')}
          </span>
          <Input value={job.title} disabled className="text-primary/60" />
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-primary/60">
              {t('jobs.translations.english')}
            </span>
            <TranslateButton
              jobId={job._id}
              sourceLanguage="sv"
              targetLanguage="en"
              text={job.title}
              isHtml={false}
              disabled={!job.title.trim()}
              onTranslated={(text) => setTitleDraft(text)}
            />
          </div>
          {titleDraft !== null && (
            <div className="flex items-center gap-1.5 rounded-md bg-amber-50 px-2.5 py-1.5 text-xs text-amber-700 dark:bg-amber-950/30 dark:text-amber-400">
              {t('jobs.translations.draftHint')}
            </div>
          )}
          <Input
            value={titleTranslation}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={(e) => {
              setTitleDraft(null)
              saveTranslation({
                jobId: job._id,
                field: 'title',
                language: 'en',
                value: e.target.value,
              })
            }}
            placeholder={t('jobs.form.titlePlaceholder')}
            className={
              titleDraft !== null
                ? 'border-amber-400 ring-1 ring-amber-400/30'
                : ''
            }
          />
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <span className="text-xs font-medium text-primary/60">
            {t('jobs.translations.swedish')}
          </span>
          <Textarea
            value={job.description}
            disabled
            className="text-primary/60 resize-none"
            rows={3}
          />
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-primary/60">
              {t('jobs.translations.english')}
            </span>
            <TranslateButton
              jobId={job._id}
              sourceLanguage="sv"
              targetLanguage="en"
              text={job.description}
              isHtml={false}
              disabled={!job.description.trim()}
              onTranslated={(text) => setDescriptionDraft(text)}
            />
          </div>
          {descriptionDraft !== null && (
            <div className="flex items-center gap-1.5 rounded-md bg-amber-50 px-2.5 py-1.5 text-xs text-amber-700 dark:bg-amber-950/30 dark:text-amber-400">
              {t('jobs.translations.draftHint')}
            </div>
          )}
          <Textarea
            value={descriptionTranslation}
            onChange={(e) => setDescriptionDraft(e.target.value)}
            onBlur={(e) => {
              setDescriptionDraft(null)
              saveTranslation({
                jobId: job._id,
                field: 'description',
                language: 'en',
                value: e.target.value,
              })
            }}
            placeholder={t('jobs.form.descriptionPlaceholder')}
            rows={3}
            className={`resize-none ${descriptionDraft !== null ? 'border-amber-400 ring-1 ring-amber-400/30' : ''}`}
          />
        </div>
      </div>
    </div>
  )
}
